// The focus modal (PRD 008 Part B). A salient, animated surfacing of the latest
// high-salience AI contribution — for the shared-screen case, where a card
// scrolling by in the feed is easy to miss. Pure UI over the existing AI slice:
// it's a second presentation of an `AiTurn` (no gateway/protocol change), gated
// by kind + the persisted focus-modal pref, exactly like the audio-cue mute.
//
// The modal is ALSO the single trigger for the AI's spoken voice: when a card
// pops here and Voice is on, it speaks (so a spoken contribution is always the
// one on screen — which is what makes it dismissable). Closing or muting the
// modal stops that audio.
//
// Stacking (OQ4): only the latest eligible turn is shown; a newer one replaces
// it. A dismissed turn never re-pops. Auto-dismisses, or close manually.

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Volume2, X } from 'lucide-react'
import { KIND_STYLE, TONE } from '@/components/shell/aiTurnStyle'
import { getFocusKinds, isFocusModalEnabled } from '@/lib/ai/focusModal'
import type { SpeechController } from '@/lib/ai/speech'
import { voicePlayer } from '@/lib/ai/voicePlayer'
import { cn } from '@/lib/utils'
import type { AiTurn } from '@/lib/ai/types'

const AUTO_DISMISS_MS = 9000

function eligible(t: AiTurn): boolean {
  if (t.streaming || t.completed || !t.text.trim()) return false
  // A speak-flagged card always surfaces here (so it can be spoken + dismissed),
  // beyond the configured focus kinds.
  return t.speak === true || getFocusKinds().includes(t.kind)
}

export function FocusModal({
  turns,
  speech,
  onJump
}: {
  turns: AiTurn[]
  /** The spoken-voice controller, when a voice is configured. */
  speech?: SpeechController | null
  /** Scroll the feed to the line this contribution refers to (its anchor). */
  onJump?: (segmentId: string | null) => void
}) {
  // The turn currently surfaced, and the set we've already shown/dismissed so a
  // re-render (or a later edit to an old turn) never re-pops it.
  const [shown, setShown] = useState<AiTurn | null>(null)
  const [open, setOpen] = useState(false)
  // The min display time has elapsed for the current card. Closing waits on this
  // AND on the voice finishing (below), so the card never vanishes mid-sentence.
  const [minElapsed, setMinElapsed] = useState(false)
  const handledRef = useRef<Set<string>>(new Set())
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Turns present at the first render were RESTORED from a prior round (reopening
  // a saved session) or were already on screen — they must never auto-pop or get
  // re-spoken. Only contributions that arrive AFTER mount surface here.
  const didInit = useRef(false)

  // Live "is this card speaking" — drives the mute/speaking control and the
  // voice-aware auto-dismiss.
  const speakingId = useSyncExternalStore(voicePlayer.subscribe, voicePlayer.current)
  const speaking = speakingId != null && speakingId === shown?.id

  useEffect(() => {
    // Seed the handled set with everything present at mount so a reopened
    // session never re-pops or re-speaks an old card; bail without surfacing.
    if (!didInit.current) {
      didInit.current = true
      for (const t of turns) handledRef.current.add(t.id)
      return
    }
    if (!isFocusModalEnabled()) return
    // Walk newest→oldest for the first eligible, not-yet-handled turn.
    for (let i = turns.length - 1; i >= 0; i--) {
      const t = turns[i]
      if (!t || handledRef.current.has(t.id)) continue
      if (!eligible(t)) continue
      handledRef.current.add(t.id)
      setShown(t)
      setOpen(true)
      setMinElapsed(false)
      // The overlay popping IS the moment to speak (if eligible). announce()
      // cuts any previous voice first, so an older card's audio never lingers.
      speech?.announce(t)
      // Start the MIN-display timer — actual close also waits on the voice (the
      // effect below), so a long spoken line isn't cut off by a short timer.
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setMinElapsed(true), AUTO_DISMISS_MS)
      break
    }
    // Mark any other freshly-arrived eligible turns handled so only the latest
    // pops (we showed the newest above; older unseen ones shouldn't queue up).
    for (const t of turns) {
      if (eligible(t)) handledRef.current.add(t.id)
    }
  }, [turns, speech])

  // Voice-aware auto-dismiss: close once the min time has passed AND this card's
  // voice is no longer playing — i.e. after max(min display, voice duration).
  // When voice is off (or this card isn't spoken) it never plays, so this closes
  // exactly at the min time, the prior behavior.
  useEffect(() => {
    if (!open || !shown || !minElapsed) return
    if (speakingId === shown.id) return // still reading it aloud — wait
    setOpen(false)
  }, [open, shown, minElapsed, speakingId])

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    []
  )

  function dismiss() {
    setOpen(false)
    if (timerRef.current) clearTimeout(timerRef.current)
    // Closing the card stops its audio (only if it's the one still playing).
    speech?.stop(shown?.id)
  }

  // Mute = silence just this interaction's audio, leave the card up.
  function mute() {
    speech?.stop(shown?.id)
  }

  if (!shown) return null
  const style = KIND_STYLE[shown.kind]
  const tone = TONE[style.tone]
  const Icon = style.Icon

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 top-20 z-50 flex justify-center px-4 transition-all duration-300',
        open ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0'
      )}
      aria-live="assertive"
    >
      <div
        role="alertdialog"
        className={cn(
          'flex max-h-[70vh] w-full max-w-md flex-col rounded-xl border border-l-4 shadow-lg backdrop-blur-sm',
          // Only catch clicks while open: a dismissed card stays mounted (it
          // fades/slides out, and lingers at opacity-0 until replaced), so a
          // static pointer-events-auto would keep eating clicks on whatever sits
          // behind it (e.g. the whiteboard control bar).
          open ? 'pointer-events-auto' : 'pointer-events-none',
          tone.border,
          tone.bg
        )}
      >
        <div className={cn('flex shrink-0 items-center gap-1.5 p-4 pb-2 text-label font-semibold tracking-wide uppercase', tone.text)}>
          <Icon className="size-4 shrink-0" />
          <span className="truncate">{style.label}</span>
          <span className="ml-auto flex items-center gap-1">
            {/* Mute just this interaction's audio (only while it's speaking). */}
            {speaking && (
              <button
                type="button"
                onClick={mute}
                title="Mute this — stop reading it aloud"
                className="grid size-6 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-surface hover:text-text"
              >
                <Volume2 className="size-4" />
              </button>
            )}
            <button
              type="button"
              onClick={dismiss}
              title="Dismiss"
              className="grid size-6 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-surface hover:text-text"
            >
              <X className="size-4" />
            </button>
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          <p className="text-body leading-relaxed whitespace-pre-wrap text-text">{shown.text}</p>
          {shown.detail && (
            <p className="mt-1 text-body-sm text-muted-foreground">↳ {shown.detail}</p>
          )}
          {onJump && (
            <button
              type="button"
              onClick={() => {
                onJump(shown.afterSegmentId)
                dismiss()
              }}
              className={cn('mt-3 text-label font-medium hover:underline', tone.text)}
            >
              Show in transcript →
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
